/**
 * Ported VERBATIM from `apr_template/src/pages/ApplicationPortfolioQA.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAPR } from '../compat/APRContext';
import { useAuth } from '../compat/AuthContext';
import { type Application } from '../compat/types';
import apiService, { authFetch } from '../compat/api';
import QAViewModal from '../components/QAViewModal';
import QAAssessmentModal, { type AssessmentResults } from '../components/QAAssessmentModal';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
// The row controls' shared look — the same rectangles, rules and inks Initial Portfolio
// Scoring uses. The stylesheet covers this screen's buttons, which already carry the
// `qa-action-btn` classes and their own currentColor SVGs; the status chip is imported as
// a component so the two screens cannot drift on it again.
import { AssessmentStatusChip } from '../components/RowControls';
import '../components/RowControls.css';
import './ApplicationPortfolioQA.css';
import ApplicationNoteModal, { ApplicationNoteButton } from '../components/ApplicationNoteModal';


/**
 * Is this application one of MINE?
 *
 * It used to be `app.appOwnerEmail !== currentUser.email` — one column, the application
 * owner's. The questionnaire is now split by dimension aspect and routed to as many as five
 * different owners, so an application can be yours through any of five email fields. APP-0468
 * named its owner in `bpo_email` alone, so the old check filtered it out and the screen was
 * empty for the very person who had been invited to answer it.
 *
 * Case-insensitive, because owner emails are free text typed into Master Data and the server
 * matches them with `lower()`. `BPO@gmail.com` and `bpo@gmail.com` are the same person, and
 * an exact comparison here would disagree with the API about who owns what.
 *
 * This mirrors the server, it does not replace it: `permittedAspectsByApplication` has already
 * scoped what the API returned. A client-side filter is presentation.
 */
const ownsApplication = (app: Application, email: string | undefined): boolean => {
  if (!email) return false;
  const mine = email.trim().toLowerCase();
  return [app.appOwnerEmail, app.toEmail, app.bpoEmail, app.securityOwnerEmail, app.executiveOwnerEmail]
    .some((candidate) => (candidate ?? '').trim().toLowerCase() === mine);
};

const ApplicationPortfolioQAHelp: Help = {
  title: 'Application Portfolio Q&A',
  icon: '📝',
  purpose:
    'Answer the assessment questionnaire for applications in <strong>In-Process</strong> APR models and runs. The questionnaire is <strong>split between up to five owners</strong> — each dimension is answered by the person accountable for it, and nobody answers outside their own area.',
  doHere: [
    'Click <strong>Start Q&amp;A</strong> to begin a questionnaire, or <strong>Continue</strong> to resume a partially answered one.',
    'Each owner sees <strong>only their own sections</strong>. A Business Process Owner opens the questionnaire and finds the Business dimensions and nothing else — the other sections are not hidden in the browser, they are never sent to it.',
    'The <strong>Progress</strong> count and the per-owner chips on each row show whose section is outstanding, so a stalled assessment can be chased to the right person rather than to everyone.',
    'Use <strong>View</strong> / <strong>View Progress</strong> to inspect answers without editing.',
    'Filter by <strong>Application ID</strong> and (Admin/Analyst) by <strong>Owner</strong>; switch between <strong>Tree</strong> (grouped by model) and <strong>Flat</strong> views.',
    'Note: only child applications are eligible; parent applications are excluded. Rows with a locked <strong>Final</strong> disposition are read-only.',
  ],
  fits:
    'Application Evaluation &rarr; Initial Disposition &rarr; Application Portfolio Q&amp;A',
  firstTime:
    'Find your application, click Start Q&amp;A, answer every question in your own sections, then Save and Close.',
  sections: [
    {
      type: 'defs',
      label: 'Who answers which dimension',
      items: [
        { term: 'Technical', color: '#0842a0', def: 'Answered by the <strong>Application Owner</strong> — architecture, technical fit and the state of the application itself.' },
        { term: 'Operational', color: '#3c4043', def: 'Answered by the <strong>Technical Owner / Custodian</strong> — how the application is run day to day.' },
        { term: 'Business', color: '#0d652d', def: 'Answered by the <strong>Business Process Owner</strong> — the value the business gets and the processes it carries.' },
        { term: 'Security', color: '#8c3a00', def: 'Answered by the <strong>Security Owner</strong> — risk, compliance and exposure.' },
        { term: 'Strategy', color: '#4a148c', def: 'Answered by the <strong>Executive Owner</strong> — where the application sits in the longer plan.' },
      ],
    },
    {
      type: 'defs',
      label: 'How the routing is decided',
      items: [
        { term: 'Set on the dimension', color: '#0842a0', def: 'Every dimension carries a required aspect, which decides who answers it. Existing dimensions default to <strong>Technical</strong>.' },
        { term: 'Overridden per framework', color: '#0842a0', def: 'A framework may route one of its dimensions to a different owner without affecting any other framework that uses it.' },
        { term: 'All owners or none', color: '#8c3a00', def: 'Invitations are refused unless <strong>every</strong> owner the model needs has a name and an email on <strong>every</strong> application in it — a half-routed questionnaire cannot be completed, so it is not started.' },
        { term: 'No score until complete', color: '#8c1d18', def: 'The overall score waits for the <strong>last</strong> question across <strong>every</strong> aspect. Owners never see a score or a disposition at any point.' },
      ],
    },
    {
      type: 'defs',
      label: 'Q&A status',
      items: [
        { term: 'Not Started', color: '#202124', def: 'No questions answered yet.' },
        { term: 'In-Process', color: '#9a3412', def: 'Some questions answered — resume with Continue.' },
        { term: 'Complete', color: '#14532d', def: 'All questions answered; scoring can be computed.' },
      ],
    },
  ],
};

// Type for grouped model data
interface ModelGroup {
  modelId: string;
  modelName: string;
  modelType: 'Model' | 'Run';
  frameworkName?: string;
  applications: Application[];
}

const ApplicationPortfolioQA: React.FC = () => {
  const { state, dispatch } = useAPR();
  const { currentUser } = useAuth();
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('All');
  // Free-text owner filter (substring match), same as the Application ID filter.
  // Empty string = no filter.
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [inProcessApplications, setInProcessApplications] = useState<Application[]>([]);
  const [applicationModels, setApplicationModels] = useState<Record<string, { name: string; type: 'Model' | 'Run' }>>({});
  const [modelGroups, setModelGroups] = useState<ModelGroup[]>([]);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  /**
   * True once the first load has been rendered.
   *
   * The tree starts collapsed, but only ONCE. Every reload after that keeps whatever
   * the user had open — see the note where this is read.
   */
  const hasLoadedOnce = useRef(false);
  /**
   * Which applications carry a note, so a row can say so without opening it.
   * Only the ids matter here; the text is fetched when the editor opens.
   */
  const [notedApps, setNotedApps] = useState<Set<string>>(new Set());
  const [noteFor, setNoteFor] = useState<{ id: string; name: string; code?: string } | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'flat'>('tree');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<{ id: string; name: string; aprRunId?: string } | null>(null);
  const [qaStatuses, setQAStatuses] = useState<Record<string, any>>({});
  // App id currently having its questionnaire generated on first "Start Q&A" click.
  const [generatingAppId, setGeneratingAppId] = useState<string | null>(null);
  const [excludedParentCount, setExcludedParentCount] = useState(0);
  const [finalDispositions, setFinalDispositions] = useState<Record<string, string | null>>({});

  // Load final dispositions to determine which applications are locked
  const loadFinalDispositions = async () => {
    try {
      const response = await authFetch('/api/final-disposition');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const dispositionMap: Record<string, string | null> = {};
          data.data.forEach((item: any) => {
            dispositionMap[item.application_id] = item.final_4r_model || null;
          });
          setFinalDispositions(dispositionMap);
          console.log('🔒 Final dispositions loaded:', dispositionMap);
        }
      }
    } catch (error) {
      console.error('Error loading final dispositions:', error);
    }
  };

  // Load data when component mounts and when APR context changes
  useEffect(() => {
    console.log('🚀 ApplicationPortfolioQA: Component mounted or context changed, loading data...');
    loadInProcessApplications();
    loadFinalDispositions();
  }, [state.models, state.aprRuns, state.applications]);

  // Which applications have a note. Reloaded with the statuses so a note saved
  // elsewhere shows up without a hard refresh.
  const loadNotedApps = async () => {
    try {
      const response = await authFetch('/api/notes');
      if (!response.ok) return;
      const data = await response.json();
      setNotedApps(new Set(Object.keys(data.notes ?? {})));
    } catch {
      // A missing note indicator must never stop the questionnaire loading.
    }
  };

  useEffect(() => {
    loadNotedApps();
  }, [inProcessApplications]);

  // Load Q&A status for applications
  useEffect(() => {
    if (inProcessApplications.length > 0) {
      console.log('📋 Applications loaded, loading Q&A statuses...');
      loadQAStatuses();
    }
  }, [inProcessApplications]);

  const loadInProcessApplications = async () => {
    setLoading(true);
    try {
      console.log('🔍 Loading In-Process applications...');
      console.log('📊 APR Context State:', {
        applications: state.applications.length,
        aprRuns: state.aprRuns.length,
        models: state.models.length
      });

      // Get applications that are in "In-Process" APR runs or models
      const inProcessAPRRuns = state.aprRuns.filter(run => run.status === 'In-Process');
      const inProcessAPRModels = state.models.filter(model => model.status === 'In-Process');

      console.log('🏃 In-Process APR Runs:', inProcessAPRRuns.map(r => ({ id: r.id, name: r.rationalizationName })));
      console.log('🎯 In-Process APR Models:', inProcessAPRModels.map(m => ({ id: m.id, name: m.name })));

      const allInProcessApps = new Map<string, Application>();
      const appModelMapping: Record<string, { name: string; type: 'Model' | 'Run' }> = {};
      const groups: ModelGroup[] = [];

      // Get applications from In-Process APR runs
      for (const run of inProcessAPRRuns) {
        if (run.selectedApplicationIds) {
          console.log(`📋 APR Run "${run.rationalizationName}" has ${run.selectedApplicationIds.length} selected apps`);
          const runApps: Application[] = [];
          for (const appId of run.selectedApplicationIds) {
            const app = state.applications.find(a => a.id === appId);
            if (app && !app.isParent) {
              allInProcessApps.set(app.id, app);
              appModelMapping[app.id] = { name: run.rationalizationName, type: 'Run' };
              runApps.push(app);
              console.log(`✅ Added app: ${app.name} (from run: ${run.rationalizationName})`);
            } else if (app && app.isParent) {
              console.log(`⏭️ Skipping parent app: ${app.name}`);
            } else {
              console.log(`❌ App not found in context: ${appId}`);
            }
          }
          if (runApps.length > 0) {
            groups.push({
              modelId: run.id,
              modelName: run.rationalizationName,
              modelType: 'Run',
              applications: runApps
            });
          }
        }
      }

      // Get applications from In-Process APR models using API
      for (const model of inProcessAPRModels) {
        try {
          console.log(`🔗 Loading applications for model: ${model.name}`);
          const modelApplications = await apiService.getModelApplications(model.id);
          console.log(`📦 Model "${model.name}" has ${modelApplications.length} applications`);
          const modelApps: Application[] = [];
          modelApplications.forEach((app: Application) => {
            if (!app.isParent) {
              allInProcessApps.set(app.id, app);
              appModelMapping[app.id] = { name: model.name, type: 'Model' };
              modelApps.push(app);
              console.log(`✅ Added app from model: ${app.name} (from model: ${model.name})`);
            }
          });
          if (modelApps.length > 0) {
            groups.push({
              modelId: model.id,
              modelName: model.name,
              modelType: 'Model',
              frameworkName: (model as any).frameworkName || undefined,
              applications: modelApps
            });
          }
        } catch (error) {
          console.error(`❌ Error loading applications for model ${model.id}:`, error);
        }
      }

      const allApps = Array.from(allInProcessApps.values());
      const excludedParentApps = state.applications.filter(app =>
        app.isParent && (
          inProcessAPRRuns.some(r => r.selectedApplicationIds?.includes(app.id)) ||
          groups.some(g => g.applications.some(a => a.id === app.id))
        )
      );

      console.log(`🎉 Total In-Process applications found: ${allApps.length}`);
      console.log(`🚫 Excluded parent applications: ${excludedParentApps.length}`);
      console.log(`✅ Eligible child applications: ${allApps.length}`);
      console.log('📱 Applications:', allApps.map(app => ({ id: app.id, name: app.name })));
      console.log('🏗️ App-Model mapping:', appModelMapping);
      console.log('🌳 Model Groups:', groups);

      setInProcessApplications(allApps);
      setExcludedParentCount(excludedParentApps.length);
      setApplicationModels(appModelMapping);
      setModelGroups(groups);

      // Collapse by default on the FIRST load only.
      //
      // This reload runs whenever the APR context changes, and saving a Q&A answer
      // triggers a rescore, which updates the context. So answering a question and
      // closing the questionnaire re-ran this and collapsed the whole tree — you came
      // back from a model you had opened deliberately and had to find it again.
      //
      // Later loads keep what was open, dropping only ids for models that no longer
      // exist, so the tree cannot hold a stale entry open forever.
      /*
       * THE DECISION IS MADE OUT HERE, NOT INSIDE THE UPDATER.
       *
       * This used to set `hasLoadedOnce.current` from inside `setExpandedModels(prev => ...)`.
       * React 18's StrictMode deliberately invokes state updaters TWICE in development to
       * surface impurity — so the first call set the flag and returned the expanded set, and
       * the second call saw the flag already true, took the "keep what the user had" branch,
       * and returned the old EMPTY set. The expansion was computed correctly and then thrown
       * away, every single time, with nothing in the code reading as wrong.
       *
       * An updater must be a pure function of `prev`. The flag is a side effect, so it
       * belongs here, where it runs once.
       */
      const firstRealLoad = !hasLoadedOnce.current && groups.length > 0;
      if (firstRealLoad) hasLoadedOnce.current = true;

      setExpandedModels(prev => {
        // Collapse by default on the FIRST load only — but only when there are enough
        // groups for a collapsed overview to be worth reading. With one model holding one
        // application, opening collapsed means a page with a title and nothing under it,
        // which reads as an empty screen rather than a closed one.
        //
        // Later loads keep what was open, dropping only ids for models that no longer
        // exist, so the tree cannot hold a stale entry open forever. That matters because
        // saving a Q&A answer triggers a rescore, which updates the context and re-runs
        // this loader — without it, answering a question collapsed the tree under you.
        if (firstRealLoad) {
          return groups.length <= 5 ? new Set(groups.map(g => g.modelId)) : new Set();
        }
        const live = new Set(groups.map(g => g.modelId));
        return new Set([...prev].filter(id => live.has(id)));
      });
    } catch (error) {
      console.error('❌ Error loading in-process applications:', error);
      setInProcessApplications([]);
      setModelGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadQAStatuses = async () => {
    try {
      console.log(`🔄 Loading QA statuses for ${inProcessApplications.length} applications (lightweight)...`);
      // Single lightweight query — just counts, no scoring data
      const statuses = await apiService.getQAStatuses();
      const statusMap: Record<string, any> = {};

      for (const row of statuses) {
        const total = parseInt(row.total_questions as any) || 0;
        const answered = parseInt(row.answered_questions as any) || 0;
        statusMap[row.application_id] = {
          totalQuestions: total,
          answeredQuestions: answered,
          completionPercentage: total > 0 ? Math.round((answered / total) * 100) : 0,
          // Who owes what. One bar cannot say whose section is outstanding, and the overall
          // score waits for the LAST question across every aspect.
          aspects: Array.isArray(row.aspects)
            ? row.aspects.map((a: any) => ({
                aspect: String(a.aspect),
                total: parseInt(a.total_questions as any) || 0,
                answered: parseInt(a.answered_questions as any) || 0,
              }))
            : [],
        };
      }

      console.log('📊 Final QA Status Map:', Object.keys(statusMap).length, 'apps');
      setQAStatuses(statusMap);
    } catch (error) {
      console.error('❌ Error loading QA statuses:', error);
    }
  };

  // Function to determine Q&A status for an application using real assessment data
  const getQAStatus = (app: Application) => {
    const appQAStatus = qaStatuses[app.id];
    
    if (!appQAStatus) {
      return { status: 'Not Started', className: 'status-not-started', icon: '⭕' };
    }
    
    const { totalQuestions, answeredQuestions } = appQAStatus;
    
    if (answeredQuestions === 0) {
      return { status: 'Not Started', className: 'status-not-started', icon: '⭕' };
    } else if (answeredQuestions < totalQuestions) {
      return { status: 'In-Process', className: 'status-in-process', icon: '⏳' };
    } else {
      return { status: 'Complete', className: 'status-complete', icon: '✅' };
    }
  };

  // Toggle model expansion in tree view
  const toggleModelExpansion = (modelId: string) => {
    setExpandedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Get aggregate stats for a model group
  const getModelStats = (group: ModelGroup) => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    let completedApps = 0;
    let inProgressApps = 0;
    let notStartedApps = 0;

    group.applications.forEach(app => {
      const status = qaStatuses[app.id];
      if (status) {
        totalQuestions += status.totalQuestions || 0;
        answeredQuestions += status.answeredQuestions || 0;
        if (status.answeredQuestions === 0) {
          notStartedApps++;
        } else if (status.answeredQuestions < status.totalQuestions) {
          inProgressApps++;
        } else {
          completedApps++;
        }
      } else {
        notStartedApps++;
      }
    });

    return {
      totalQuestions,
      answeredQuestions,
      completedApps,
      inProgressApps,
      notStartedApps,
      totalApps: group.applications.length,
      completionPercentage: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
    };
  };

  // Filter applications within a model group
  const filterGroupApplications = (apps: Application[]) => {
    return apps.filter(app => {
      // Role-based access control
      if (currentUser?.role === 'Application Owner') {
        if (!ownsApplication(app, currentUser.email)) {
          return false;
        }
      }
      // Application ID filter
      if (selectedApplicationId !== 'All') {
        const filterText = selectedApplicationId.toLowerCase();
        const applicationIdMatch = (app.applicationIdCode && app.applicationIdCode.toLowerCase().includes(filterText)) ||
                                   app.name.toLowerCase().includes(filterText);
        if (!applicationIdMatch) {
          return false;
        }
      }
      // Application Owner filter
      if ((currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && selectedOwner.trim()) {
        if (!(app.applicationOwner || '').toLowerCase().includes(selectedOwner.trim().toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  };

  // Get applications that the current user can see (for statistics)
  const visibleApplications = inProcessApplications.filter(app => {
    // Role-based access control
    if (currentUser?.role === 'Application Owner') {
      return ownsApplication(app, currentUser?.email);
    }
    // Administrators and Users can see all applications
    return true;
  });

  // Apply role-based filtering and other filters
  const filteredApplications = inProcessApplications.filter(app => {
    // Role-based access control
    if (currentUser?.role === 'Application Owner') {
      // Application Owners can only see their own applications — through ANY owner slot.
      if (!ownsApplication(app, currentUser.email)) {
        return false;
      }
    }
    // Administrators and Users can see and conduct Q&A on all In-Process applications regardless of ownership
    
    // Application ID filter - support partial text matching
    if (selectedApplicationId !== 'All') {
      const filterText = selectedApplicationId.toLowerCase();
      const applicationIdMatch = (app.applicationIdCode && app.applicationIdCode.toLowerCase().includes(filterText)) ||
                                 app.name.toLowerCase().includes(filterText);
      if (!applicationIdMatch) {
        return false;
      }
    }
    
    // Application Owner filter (only for Administrator and APR Analyst roles)
    if ((currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && selectedOwner.trim()) {
      if (!(app.applicationOwner || '').toLowerCase().includes(selectedOwner.trim().toLowerCase())) {
        return false;
      }
    }
    
    return true;
  });

  const handleViewQA = (application: Application, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    console.log('ApplicationPortfolioQA: handleViewQA called for', application.name);
    
    // Prevent opening if modal is already open
    if (showViewModal) {
      console.log('ApplicationPortfolioQA: Modal already open, ignoring');
      return;
    }
    
    // Find the APR run ID for this application
    let aprRunId: string | undefined;
    
    // First check in APR runs
    const aprRun = state.aprRuns.find(run => 
      run.status === 'In-Process' && 
      run.selectedApplicationIds?.includes(application.id)
    );
    
    if (aprRun) {
      aprRunId = aprRun.id;
    }
    
    console.log('ApplicationPortfolioQA: Setting selected application:', {
      id: application.id,
      name: application.name,
      aprRunId
    });
    
    // Set selected application and show modal immediately
    setSelectedApplication({
      id: application.id,
      name: application.name,
      aprRunId
    });
    
    console.log('ApplicationPortfolioQA: Setting showViewModal to true');
    setShowViewModal(true);
  };

  const handleStartQA = async (application: Application, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent opening if already opening or modal is already open
    if (showQAModal || generatingAppId) return;

    // Find the APR run ID for this application
    let aprRunId: string | undefined;

    // First check in APR runs
    const aprRun = state.aprRuns.find(run =>
      run.status === 'In-Process' &&
      run.selectedApplicationIds?.includes(application.id)
    );

    if (aprRun) {
      aprRunId = aprRun.id;
    }

    // Auto-generate the questionnaire the first time Q&A is started for this app.
    // The qa_assessments rows (one per framework question) are what every Q&A screen
    // reads; scheduling a model doesn't create them, so we generate on demand here.
    // The server endpoint is idempotent (skips questions that already have a record),
    // so a Continue click safely re-runs without duplicating.
    const currentStatus = qaStatuses[application.id];
    if (!currentStatus || !currentStatus.totalQuestions) {
      // Resolve the owning APR Model so the endpoint uses that model's framework.
      const group = modelGroups.find(g =>
        g.modelType === 'Model' && g.applications.some(a => a.id === application.id)
      );
      const aprModelId = group?.modelId;
      try {
        setGeneratingAppId(application.id);
        await apiService.initializeQAAssessments(
          [{
            id: application.id,
            name: application.name,
            customerId: (application as any).customer_id,
            applicationIdCode: (application as any).application_id_code
          }],
          aprRunId,
          aprModelId
        );
        await loadQAStatuses();
      } catch (error: any) {
        // Fail fast: surface the error and do not open an empty questionnaire.
        console.error('❌ Failed to generate Q&A questionnaire:', error);
        alert(`Could not prepare the questionnaire: ${error?.response?.data?.error || error?.message || 'Unknown error'}`);
        setGeneratingAppId(null);
        return;
      }
      setGeneratingAppId(null);
    }

    // Set selected application and show modal
    setSelectedApplication({
      id: application.id,
      name: application.name,
      aprRunId
    });

    setShowQAModal(true);
  };

  // Function to check if APR Model can be reverted (all apps have "Not Started" Q&A status)
  const canRevertModel = (modelId: string) => {
    const modelApplications = inProcessApplications.filter(app => {
      // Check if app belongs to this model
      return state.models.find(m => m.id === modelId && m.status === 'In-Process');
    });
    
    // All applications must have "Not Started" status
    return modelApplications.every(app => getQAStatus(app).status === 'Not Started');
  };

  // Function to revert APR Model back to Scheduled status
  const handleRevertModel = async (modelId: string, modelName: string) => {
    if (!window.confirm(`Are you sure you want to revert "${modelName}" back to Scheduled status? This will remove all applications from the Q&A process.`)) {
      return;
    }

    try {
      // Find the current model to get all its properties
      const currentModel = state.models.find(m => m.id === modelId);
      if (!currentModel) {
        alert('APR Model not found.');
        return;
      }

      // Create updated model with all required properties
      const updatedModel = {
        ...currentModel,
        status: 'Scheduled' as const
      };

      await apiService.updateModel(modelId, {
        name: currentModel.name,
        status: 'Scheduled'
      });
      
      // Update local state
      dispatch({
        type: 'UPDATE_MODEL',
        payload: updatedModel
      });
      
      // Refresh applications list
      loadInProcessApplications();
      
      alert(`APR Model "${modelName}" has been successfully reverted to Scheduled status.`);
    } catch (error) {
      console.error('Error reverting model:', error);
      alert('Failed to revert APR Model status. Please try again.');
    }
  };

  // Modal event handlers
  const handleCloseViewModal = async () => {
    console.log('🔄 Modal closed, refreshing QA statuses...');
    setShowViewModal(false);
    setSelectedApplication(null);
    // Refresh the QA statuses after closing modal
    if (inProcessApplications.length > 0) {
      await loadQAStatuses();
    }
  };

  const handleCloseQAModal = async () => {
    setShowQAModal(false);
    setSelectedApplication(null);
    // Refresh the QA statuses after closing modal
    if (inProcessApplications.length > 0) {
      await loadQAStatuses();
    }
  };

  const handleCompleteAssessment = async (results: AssessmentResults) => {
    console.log('Assessment completed:', results);
    await handleCloseQAModal();
  };

  return (
    <div className="page page-fixed-header">
      <HelpPanel help={ApplicationPortfolioQAHelp} />
      {/* Fixed Header Section - Does NOT scroll */}
      <div className="fixed-header-section">
        {/* Header */}
        <PageHeader
          title="Application Portfolio Q&A"
          subtitle='Answer questionnaire for applications in "In-Process" APR models and runs'
        />

        {/* Notice about excluded parent applications */}
        {!loading && excludedParentCount > 0 && (
          <div style={{
            marginBottom: '8px',
            padding: '8px 12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '6px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#92400e', fontSize: '13px' }}>
              <span>ℹ️</span>
              <span>
                <strong>{excludedParentCount} parent application{excludedParentCount > 1 ? 's' : ''}</strong> excluded.
                Only child applications are eligible for Q&A.
              </span>
            </div>
          </div>
        )}

        {/* Filter Section - Closer to header */}
        {!loading && (
          <div className="md-filter-container" style={{
            marginBottom: '0',
            padding: '10px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
          {/* Application ID Filter */}
          <div className="md-filter-section" style={{ flex: 1, position: 'relative' }}>
            <label htmlFor="applicationIdFilter" className="md-filter-label" style={{
              display: 'block',
              marginBottom: '5px',
              fontWeight: '500',
              fontSize: '14px'
            }}>Filter by Application ID</label>
            <input
              type="text"
              id="applicationIdFilter"
              value={selectedApplicationId === 'All' ? '' : selectedApplicationId}
              onChange={(e) => setSelectedApplicationId(e.target.value || 'All')}
              placeholder="Enter Application ID..."
              className="md-input"
              /* Sizing comes from .md-input so this lines up with the selects.
                 Only the extra right padding is local — it clears the × button. */
              style={{ paddingRight: '34px' }}
            />
            {selectedApplicationId !== 'All' && (
              <button
                onClick={() => setSelectedApplicationId('All')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  /* Anchor to the input's bottom edge, not a hard-coded top: the
                     input is the last child, so this stays centred in it. */
                  bottom: '12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#666',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Clear Application ID filter"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Application Owner Filter - Only show for Administrator and APR Analyst roles */}
          {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && (
            <div className="md-filter-section" style={{ flex: 1, position: 'relative' }}>
              <label htmlFor="ownerFilter" className="md-filter-label" style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '500',
                fontSize: '14px'
              }}>Filter by Application Owner</label>
              <input
                type="text"
                id="ownerFilter"
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                placeholder="Enter Application Owner..."
                className="md-input"
                style={{ paddingRight: '34px' }}
              />
              {selectedOwner && (
                <button
                  onClick={() => setSelectedOwner('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    bottom: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#666',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Clear Application Owner filter"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="md-filter-section" style={{ minWidth: '180px' }}>
            <label className="md-filter-label" style={{
              display: 'block',
              marginBottom: '5px',
              fontWeight: '500',
              fontSize: '14px'
            }}>View Mode</label>
            <div style={{
              display: 'flex',
              gap: '4px',
              backgroundColor: '#e5e5e5',
              borderRadius: '6px',
              padding: '4px',
              /* Match the 44px of .md-input / .md-select so the whole filter row
                 sits on one line — the row is align-items:center, so a shorter
                 control here also drags its label out of alignment. */
              height: '44px',
              boxSizing: 'border-box'
            }}>
              <button
                onClick={() => setViewMode('tree')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: viewMode === 'tree' ? '#fff' : 'transparent',
                  color: viewMode === 'tree' ? '#1a73e8' : '#666',
                  boxShadow: viewMode === 'tree' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                🌳 Tree
              </button>
              <button
                onClick={() => setViewMode('flat')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: viewMode === 'flat' ? '#fff' : 'transparent',
                  color: viewMode === 'flat' ? '#1a73e8' : '#666',
                  boxShadow: viewMode === 'flat' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📋 Flat
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
      {/* End Fixed Header Section */}

      {/* Scrollable Data Section */}
      <div className="scrollable-data-section">
        {/* Loading State */}
        {loading && (
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Applications for Q&A</h3>
            <p>Please wait while we fetch applications from In-Process APR models...</p>
          </div>
        )}

        {/* Applications Display - Tree or Flat View */}
        {!loading && modelGroups.length === 0 && filteredApplications.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">❓</div>
            <h3 className="md-empty-state__title">No Applications Available for Q&A</h3>
            <p className="md-empty-state__description">
              {inProcessApplications.length === 0
                ? 'No applications are currently in "In-Process" status. Start Q&A on an APR model to see applications here.'
                : selectedApplicationId !== 'All'
                  ? 'No in-process applications match the selected Application ID filter.'
                  : 'No in-process applications available for Q&A.'
              }
            </p>
          </div>
        ) : !loading && viewMode === 'tree' ? (
          /* TREE VIEW */
          <div className="tree-view-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {modelGroups.map(group => {
              const filteredApps = filterGroupApplications(group.applications);
              if (filteredApps.length === 0) return null;
              const isExpanded = expandedModels.has(group.modelId);
              const stats = getModelStats({ ...group, applications: filteredApps });

              return (
                <div key={group.modelId} className="model-tree-node" style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  {/* Model Header (Parent Node) */}
                  <div
                    onClick={() => toggleModelExpansion(group.modelId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '14px 16px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {/* Expand/Collapse Icon */}
                    <span style={{
                      marginRight: '12px',
                      fontSize: '14px',
                      color: '#666',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>

                    {/* Model Icon & Name */}
                    <span style={{ marginRight: '10px', fontSize: '1.3rem' }}>
                      {group.modelType === 'Model' ? '📁' : '🏃'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: '#333' }}>
                        {group.modelName}
                      </div>
                      {group.frameworkName && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          Framework: {group.frameworkName}
                        </div>
                      )}
                    </div>

                    {/* Stats Badges */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span className="md-count-chip md-count-chip--total">
                        {stats.totalApps} apps
                      </span>
                      {stats.completedApps > 0 && (
                        <span className="md-count-chip md-count-chip--done">
                          {stats.completedApps} complete
                        </span>
                      )}
                      {stats.inProgressApps > 0 && (
                        <span className="md-count-chip md-count-chip--progress">
                          {stats.inProgressApps} in progress
                        </span>
                      )}
                      <div style={{
                        width: '80px',
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${stats.completionPercentage}%`,
                          height: '100%',
                          backgroundColor: stats.completionPercentage === 100 ? '#4caf50' : '#1a73e8',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '35px' }}>
                        {stats.completionPercentage}%
                      </span>
                    </div>
                  </div>

                  {/* Applications Table (Children) */}
                  {isExpanded && (
                    <div className="md-table-container" style={{ borderRadius: 0 }}>
                      <table className="md-table" style={{ marginBottom: 0 }}>
                        <thead className="md-table__header">
                          <tr>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '28%' }}>Application</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '18%' }}>Owner</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '100px' }}>Q&A Status</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '100px', textAlign: 'center' }}>Progress</th>
                            <th className="md-table__cell md-table__cell--header md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody className="md-table__body">
                          {filteredApps.map((app, index) => {
                            const qaStatus = getQAStatus(app);
                            const appQAStatus = qaStatuses[app.id];
                            const answeredQuestions = appQAStatus?.answeredQuestions || 0;
                            const totalQuestions = appQAStatus?.totalQuestions || 0;
                            const isComplete = qaStatus.status === 'Complete';
                            const isInProcess = qaStatus.status === 'In-Process';
                            const isNotStarted = qaStatus.status === 'Not Started';
                            const hasDisposition = !!finalDispositions[app.id];
                            const isLocked = hasDisposition;

                            return (
                              <tr
                                key={app.id}
                                className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                                style={isLocked ? {
                                  opacity: 0.6,
                                  backgroundColor: '#f5f5f5'
                                } : undefined}
                                title={isLocked ? `Final Disposition set: ${finalDispositions[app.id]} - Assessment changes disabled` : undefined}
                              >
                                <td className="md-table__cell" style={{ width: '28%' }}>
                                  <div className="md-app-info">
                                    <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.name}</div>
                                    {app.applicationIdCode && (
                                      <div className="md-app-info__secondary" style={{ fontSize: '13px', color: '#666' }}>ID: {app.applicationIdCode}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="md-table__cell" style={{ width: '18%' }}>
                                  <div className="md-owner-info">
                                    <div className="md-owner-info__name" style={{ fontSize: '14px' }}>{app.applicationOwner || 'Not assigned'}</div>
                                  </div>
                                </td>
                                <td className="md-table__cell" style={{ width: '100px' }}>
                                  {/* The same chip Initial Portfolio Scoring uses — a tick
                                      when every question is answered, a quarter ring while
                                      it is in progress, an empty ring before it starts.
                                      This cell was plain grey text, so a 40/40 application
                                      read exactly like an untouched one apart from the
                                      word itself. */}
                                  <AssessmentStatusChip status={qaStatus.status} />
                                </td>
                                <td className="md-table__cell" style={{ width: '100px', textAlign: 'center' }}>
                                  {/* Count plus bar, matching Initial Portfolio Scoring.
                                      No percentage: the bar is the percentage. */}
                                  <div className="md-progress-info">
                                    <div className="md-progress-text">
                                      {answeredQuestions}/{totalQuestions}
                                    </div>
                                    <div className="md-progress-bar">
                                      <div
                                        className="md-progress-bar__fill"
                                        style={{ width: `${totalQuestions ? (answeredQuestions / totalQuestions) * 100 : 0}%` }}
                                      />
                                    </div>
                                    {/*
                                      PER-ASPECT BREAKDOWN. Only when the questionnaire is
                                      actually split — one aspect is the old single-owner
                                      case, and a lone row labelled "Technical 8/8" under
                                      the bar would be noise repeating what the bar says.
                                    */}
                                    {appQAStatus?.aspects?.length > 1 && (
                                      <div className="qa-aspect-breakdown">
                                        {appQAStatus.aspects.map((a: any) => {
                                          const done = a.total > 0 && a.answered === a.total;
                                          return (
                                            <span
                                              key={a.aspect}
                                              className={`qa-aspect-pill ${done ? 'qa-aspect-pill--done' : ''}`}
                                              title={`${a.aspect}: ${a.answered} of ${a.total} answered`}
                                            >
                                              {a.aspect} {a.answered}/{a.total}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="md-table__cell md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>
                                  <div className="qa-action-buttons md-action-row">
                                    <ApplicationNoteButton
                                      hasNote={notedApps.has(app.id)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNoteFor({ id: app.id, name: app.name, code: app.applicationIdCode });
                                      }}
                                    />
                                    {!isLocked && isNotStarted && (
                                      <button
                                        className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                        onClick={(e) => handleStartQA(app, e)}
                                        title="Start Q&A assessment"
                                        style={{ fontSize: '14px' }}
                                        disabled={generatingAppId === app.id}
                                      >
                                        <span className="qa-action-btn__text">
                                          {generatingAppId === app.id ? 'Preparing…' : 'Start Q&A'}
                                        </span>
                                      </button>
                                    )}
                                    {!isLocked && isInProcess && (
                                      <>
                                        <button
                                          className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                          onClick={(e) => handleStartQA(app, e)}
                                          title="Continue Q&A assessment"
                                          style={{ fontSize: '14px' }}
                                        >
                                          <span className="qa-action-btn__text">Continue</span>
                                        </button>
                                        <button
                                          className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                          onClick={(e) => handleViewQA(app, e)}
                                          title="View progress"
                                          style={{ fontSize: '14px' }}
                                        >
                                          <span className="qa-action-btn__text">View</span>
                                        </button>
                                      </>
                                    )}
                                    {(isComplete || isLocked) && (
                                      <button
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => handleViewQA(app, e)}
                                        title={isLocked ? "View assessment (Locked)" : "View assessment"}
                                        style={{ fontSize: '14px' }}
                                      >
                                        <span className="qa-action-btn__text">View</span>
                                      </button>
                                    )}
                                    {isLocked && (
                                      <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                                        🔒 Final
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !loading && (
          /* FLAT VIEW (Original Table) */
          <div className="md-card md-card--elevated">
            <div className="md-table-container">
              <table className="md-table">
                <thead className="md-table__header">
                  <tr>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '28%' }}>Application</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '18%' }}>Owner</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '100px' }}>Q&A Status</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '100px', textAlign: 'center' }}>Progress</th>
                    <th className="md-table__cell md-table__cell--header md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="md-table__body">
                  {filteredApplications.map((app, index) => {
                    const hasDisposition = !!finalDispositions[app.id];
                    const isLocked = hasDisposition;
                    return (
                    <tr
                      key={app.id}
                      className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                      style={isLocked ? {
                        opacity: 0.6,
                        backgroundColor: '#f5f5f5'
                      } : undefined}
                      title={isLocked ? `Final Disposition set: ${finalDispositions[app.id]} - Assessment changes disabled` : undefined}
                    >
                      <td className="md-table__cell" style={{ width: '28%' }}>
                        <div className="md-app-info">
                          <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.name}</div>
                          {app.applicationIdCode && (
                            <div className="md-app-info__secondary" style={{ fontSize: '13px', color: '#666' }}>ID: {app.applicationIdCode}</div>
                          )}
                        </div>
                      </td>

                      <td className="md-table__cell" style={{ width: '18%' }}>
                        <div className="md-owner-info">
                          <div className="md-owner-info__name" style={{ fontSize: '14px' }}>{app.applicationOwner || 'Not assigned'}</div>
                        </div>
                      </td>

                      <td className="md-table__cell" style={{ width: '100px' }}>
                        {(() => {
                          const qaStatus = getQAStatus(app);

                          return <AssessmentStatusChip status={qaStatus.status} />;
                        })()}
                      </td>

                      <td className="md-table__cell" style={{ width: '100px', textAlign: 'center' }}>
                        {(() => {
                          const appQAStatus = qaStatuses[app.id];
                          const answeredQuestions = appQAStatus?.answeredQuestions || 0;
                          const totalQuestions = appQAStatus?.totalQuestions || 0;

                          return (
                            <div className="md-progress-info">
                              <div className="md-progress-text">
                                {answeredQuestions}/{totalQuestions}
                              </div>
                              <div className="md-progress-bar">
                                <div
                                  className="md-progress-bar__fill"
                                  style={{ width: `${totalQuestions ? (answeredQuestions / totalQuestions) * 100 : 0}%` }}
                                />
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      <td className="md-table__cell md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>
                        {(() => {
                          const qaStatus = getQAStatus(app);
                          const isComplete = qaStatus.status === 'Complete';
                          const isInProcess = qaStatus.status === 'In-Process';
                          const isNotStarted = qaStatus.status === 'Not Started';

                          console.log('ApplicationPortfolioQA: Rendering action buttons for', app.name, 'Q&A Status:', qaStatus, 'isLocked:', isLocked);

                          // If locked due to final disposition, only show View button
                          if (isLocked) {
                            return (
                              <div className="qa-action-buttons md-action-row">
                                <ApplicationNoteButton
                                  hasNote={notedApps.has(app.id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNoteFor({ id: app.id, name: app.name, code: app.applicationIdCode });
                                  }}
                                />
                                <button
                                  className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                  onClick={(e) => handleViewQA(app, e)}
                                  title="View assessment (Locked)"
                                >
                                  <span className="qa-action-btn__text">View</span>
                                </button>
                                <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                                  🔒 Final
                                </span>
                              </div>
                            );
                          }

                          return (
                            <div className="qa-action-buttons md-action-row">
                              <ApplicationNoteButton
                                hasNote={notedApps.has(app.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNoteFor({ id: app.id, name: app.name, code: app.applicationIdCode });
                                }}
                              />
                              {/* For Application Owners - show appropriate buttons based on status */}
                              {currentUser?.role === 'Application Owner' && (
                                <>
                                  {isNotStarted && (
                                    <button
                                      className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: Start button clicked for', app.name);
                                        handleStartQA(app, e);
                                      }}
                                      title="Start Q&A assessment for this application"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polygon points="5,3 19,12 5,21"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">Start Q&A</span>
                                    </button>
                                  )}

                                  {isInProcess && (
                                    <>
                                      <button
                                        className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: Continue button clicked for', app.name);
                                          handleStartQA(app, e);
                                        }}
                                        title="Continue Q&A assessment for this application"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polygon points="5,3 19,12 5,21"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">Continue Q&A</span>
                                      </button>
                                      <button
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                          handleViewQA(app, e);
                                        }}
                                        title="View current Q&A assessment progress"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">View Progress</span>
                                      </button>
                                    </>
                                  )}

                                  {isComplete && (
                                    <button
                                      className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                        handleViewQA(app, e);
                                      }}
                                      title="View completed Q&A assessment"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                          <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">View Assessment</span>
                                    </button>
                                  )}
                                </>
                              )}

                              {/* For Administrators and APR Analysts - show same functionality as Application Owners */}
                              {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && (
                                <>
                                  {isNotStarted && (
                                    <button
                                      className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: Start button clicked for', app.name);
                                        handleStartQA(app, e);
                                      }}
                                      title="Start Q&A assessment for this application"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polygon points="5,3 19,12 5,21"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">Start Q&A</span>
                                    </button>
                                  )}
                                  
                                  {isInProcess && (
                                    <>
                                      <button 
                                        className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: Continue button clicked for', app.name);
                                          handleStartQA(app, e);
                                        }}
                                        title="Continue Q&A assessment for this application"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polygon points="5,3 19,12 5,21"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">Continue Q&A</span>
                                      </button>
                                      <button 
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                          handleViewQA(app, e);
                                        }}
                                        title="View current Q&A assessment progress"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">View Progress</span>
                                      </button>
                                    </>
                                  )}
                                  
                                  {isComplete && (
                                    <button 
                                      className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                        handleViewQA(app, e);
                                      }}
                                      title="View completed Q&A assessment"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                          <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">View Assessment</span>
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* End Scrollable Data Section */}

      {/* Q&A View Modal */}
      {noteFor && (
        <ApplicationNoteModal
          applicationId={noteFor.id}
          applicationName={noteFor.name}
          applicationCode={noteFor.code}
          onClose={() => setNoteFor(null)}
          onSaved={(id, note) =>
            setNotedApps((prev) => {
              const next = new Set(prev);
              if (note.trim()) next.add(id);
              else next.delete(id);
              return next;
            })
          }
        />
      )}

      {showViewModal && selectedApplication && (
        <>
          {console.log('ApplicationPortfolioQA: Rendering QAViewModal with props:', {
            applicationId: selectedApplication.id,
            applicationName: selectedApplication.name,
            aprRunId: selectedApplication.aprRunId,
            isOpen: showViewModal
          })}
          <QAViewModal
            applicationId={selectedApplication.id}
            applicationName={selectedApplication.name}
            aprRunId={selectedApplication.aprRunId}
            isOpen={showViewModal}
            onClose={handleCloseViewModal}
          />
        </>
      )}

      {/* Q&A Assessment Modal */}
      {showQAModal && selectedApplication && (
        <QAAssessmentModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          aprRunId={selectedApplication.aprRunId}
          isOpen={showQAModal}
          onClose={handleCloseQAModal}
          onComplete={handleCompleteAssessment}
        />
      )}
    </div>
  );
};

export default ApplicationPortfolioQA;